import React from 'react';
import PropTypes from 'prop-types';
import Link from '@components/link';
import { slugify } from '@lib/utils';

// ---------------------------------------------------------

import {
  faq_block,
  heading,
  faq_list,
  faq_item,
  question_text,
  answer_text
} from './styles.module.scss';

/**
 * Always-visible, crawlable Q&A block used for AEO. Renders semantic
 * definition-list markup so the on-page content matches the FAQPage JSON-LD
 * emitted separately via generateFAQSchema. Returns null when there are no items.
 */
const FaqBlock = ({ title, items }) => {
  const validItems = (items || []).filter(
    (item) => item && item.question && item.answer && String(item.answer).trim()
  );

  if (validItems.length === 0) return null;

  const headingId = `faq-${slugify(title || 'faq')}`;

  return (
    <section className={faq_block} aria-labelledby={headingId}>
      {title && (
        <h2 id={headingId} className={heading}>
          {title}
        </h2>
      )}
      <dl className={faq_list}>
        {validItems.map((item, idx) => (
          <div className={faq_item} key={`faq-item-${idx}`}>
            <dt className={question_text}>{item.question}</dt>
            <dd className={answer_text}>
              {item.answer}
              {item.link && (
                <>
                  {' '}
                  <Link url={item.link} ariaLabel={item.linkLabel || item.question}>
                    {item.linkLabel || 'View listings'}
                  </Link>
                </>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

FaqBlock.propTypes = {
  /**
   * Heading rendered above the Q&A list.
   */
  title: PropTypes.string,

  /**
   * Q&A items. `answer` is plain text; optional `link` renders a visible anchor
   * in the answer (ignored by the JSON-LD schema).
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string,
      answer: PropTypes.string,
      link: PropTypes.string,
      linkLabel: PropTypes.string
    })
  )
};

export default FaqBlock;
